import {fromJsonStrict, type JsonValue} from '@github-ui/dotcom-schema/protobuf'
import {
  AgentMergeActivityNotificationSchema,
  AgentMergeStatusResponseSchema,
  AgentMergeStatusResponse_Selection as Selection,
} from '@github-ui/dotcom-schema/types/copilot_sessions/v1/agent_merge'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {sendCustomMetric} from '@github-ui/stats'
import {useAlive} from '@github-ui/use-alive'
import {useCallback, useEffect, useMemo, useSyncExternalStore} from 'react'

import {agentMergeActivityStore} from '../helpers/agent-merge-activity-store'
import {sameScope} from '../helpers/agent-merge-activity-reducer'
import {agentMergeMutationKeyPrefix, agentMergeQueryKey} from '../page-data/loaders/use-agent-merge-page-data'

export function useAgentMergeActivity({
  enabled,
  headOid,
  revision,
  configurationGeneration,
  pullRequestChannel,
}: {
  enabled: boolean
  headOid: string
  revision: string
  configurationGeneration?: string
  pullRequestChannel?: string | null
}) {
  const url = usePageDataUrl(PageData.agentMergeStatus)
  const configurationUrl = usePageDataUrl(PageData.agentMerge)
  const client = useQueryClient()
  const store = useMemo(() => agentMergeActivityStore(client, url), [client, url])
  const presentation = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot)
  // This endpoint is proto-only. Its validation must fail closed independently of
  // the experimental react_enforce_schema flag, and live state is never persisted.
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister
  const query = useQuery({
    queryKey: [PageData.agentMergeStatus, url, headOid, revision, configurationGeneration],
    enabled,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async ({signal}) => {
      // The shared schema fallback can return unparsed JSON; live status must fail closed.
      // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema
      const json = await queryFnFetch<JsonValue>({queryDeps: {pathname: url, init: {signal}}})
      const response = fromJsonStrict(AgentMergeStatusResponseSchema, json, {ignoreUnknownFields: true})
      if (!response.context?.scope || response.context.headOid !== headOid) {
        throw new Error('Agent Merge status does not match the current pull request head')
      }
      const scope = response.context.scope
      if (!scope.namespace || scope.repositoryId <= 0n || scope.pullRequestNumber <= 0n || scope.pullRequestId <= 0n) {
        throw new Error('Agent Merge status is missing its pull request scope')
      }
      if (response.selection === Selection.SELECTED && !response.selectedSession?.jobId) {
        throw new Error('Agent Merge status is missing the selected job identity')
      }
      const generation = BigInt(configurationGeneration || 0)
      if (
        generation &&
        response.context.configurationGeneration &&
        response.context.configurationGeneration < generation
      ) {
        throw new Error('Agent Merge status predates the saved configuration')
      }
      response.context.configurationGeneration ||= generation || undefined
      return response
    },
  })
  const {refetch} = query
  const refresh = useCallback(() => {
    void refetch()
  }, [refetch])
  const reconcile = useCallback(() => {
    store.reconcile()
    refresh()
    if (client.isMutating({mutationKey: agentMergeMutationKeyPrefix(configurationUrl)}) === 0) {
      void client.invalidateQueries({queryKey: agentMergeQueryKey(configurationUrl, headOid)})
    }
  }, [store, refresh, client, configurationUrl, headOid])
  useEffect(() => {
    store.configure(JSON.stringify([enabled, headOid, revision]))
  }, [store, enabled, headOid, revision])
  useEffect(() => {
    if (query.isError) store.unavailable()
    else if (query.data && enabled && !query.isFetching) store.hydrate(query.data)
  }, [store, query.data, query.dataUpdatedAt, query.isError, query.isFetching, enabled])

  const receive = useCallback(
    (data: JsonValue) => {
      let notification
      try {
        notification = fromJsonStrict(AgentMergeActivityNotificationSchema, data, {ignoreUnknownFields: true})
      } catch {
        sendCustomMetric({
          name: 'REACT_QUERY_SCHEMA_ERROR',
          value: 1,
          tags: {routeId: 'mergebox', queryName: 'agent_merge_activity'},
        })
        return
      }
      if (!sameScope(store.state.context?.scope, notification.scope)) return
      store.receive(notification)
      refresh()
    },
    [store, refresh],
  )

  useAlive(enabled && !store.disabled && !query.isError ? query.data?.activityChannel : null, receive)
  useAlive(enabled ? pullRequestChannel : null, refresh)
  useEffect(() => {
    if (!enabled) return
    const restore = () => {
      store.tick()
      if (document.visibilityState === 'visible') reconcile()
    }
    window.addEventListener('focus', restore)
    window.addEventListener('online', restore)
    window.addEventListener('offline', store.reconcile)
    document.addEventListener('visibilitychange', restore)
    return () => {
      window.removeEventListener('focus', restore)
      window.removeEventListener('online', restore)
      window.removeEventListener('offline', store.reconcile)
      document.removeEventListener('visibilitychange', restore)
    }
  }, [enabled, reconcile, store])
  return {
    presentation,
    claimAnnouncement: store.claimAnnouncement,
    isError: query.isError,
    isPending: query.isPending,
    isFetching: query.isFetching,
    error: query.error,
  }
}
